// components/logistics/JobCompletionModal.jsx
import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, Clock, Award } from 'lucide-react';
import Modal from '../common/Modal';

const JobCompletionModal = ({ 
  isOpen, 
  onClose, 
  job, 
  onConfirm,
  isAdmin = false
}) => {
  const [agreed, setAgreed] = useState(false);
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);

  const handleComplete = async () => {
    if (!agreed) return;
    
    setLoading(true);
    try {
      await onConfirm({
        jobId: job.id,
        rating: isAdmin ? rating : undefined,
        comments: comments,
        completedAt: new Date().toISOString()
      });
      onClose();
    } catch (error) {
      console.error('Error completing job:', error);
    } finally {
      setLoading(false);
    }
  };

  const trustImpact = job?.requiredSkills?.[0]?.tier ? 
    0.05 + (job.requiredSkills[0].tier * 0.02) : 0.05;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isAdmin ? "Approve Job Completion" : "Complete Job"}
      size="medium"
    >
      <div className="space-y-4">
        {/* Job Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-lg mb-2">{job?.title}</h3>
          <p className="text-sm text-gray-600 mb-3">{job?.groupTitle}</p>
          
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <Clock size={14} className="mr-2 text-gray-500" />
              <span>Started: {new Date(job?.claimedAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center text-sm">
              <Award size={14} className="mr-2 text-gray-500" />
              <span className="font-medium">{job?.tokenReward} tokens</span>
            </div>
          </div>
        </div>

        {/* Completion Impact */}
        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
          <h4 className="font-medium text-green-800 mb-2">Completion Rewards</h4>
          <ul className="space-y-1 text-sm text-green-700">
            <li className="flex items-center">
              <CheckCircle size={14} className="mr-2" />
              Earn {job?.tokenReward} tokens immediately
            </li>
            <li className="flex items-center">
              <CheckCircle size={14} className="mr-2" />
              Trust score increase: +{trustImpact.toFixed(2)}
            </li>
            <li className="flex items-center">
              <CheckCircle size={14} className="mr-2" />
              Build reputation for {job?.requiredSkills?.[0]?.name} jobs
            </li>
          </ul>
        </div>

        {/* Admin Rating (if admin) */}
        {isAdmin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rate Worker Performance
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`text-2xl ${
                    star <= rating ? 'text-yellow-500' : 'text-gray-300'
                  } hover:text-yellow-400 transition-colors`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {isAdmin ? 'Feedback for Worker' : 'Completion Notes (Optional)'}
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            placeholder={isAdmin ? 'Great work on...' : 'Any notes about the job...'}
          />
        </div>

        {/* Terms Agreement */}
        <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-1 mr-3"
            />
            <div className="text-sm">
              <p className="font-medium text-yellow-800">
                {isAdmin ? 'Admin Confirmation' : 'Worker Confirmation'}
              </p>
              <p className="text-yellow-700 mt-1">
                {isAdmin 
                  ? 'I confirm that this job has been completed satisfactorily and approve token distribution.'
                  : 'I confirm that I have completed this job according to the requirements.'
                }
              </p>
            </div>
          </label>
        </div>

        {/* Warning for Early Completion */}
        {job?.estimatedHours && (
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <p>
              This job was estimated at {job.estimatedHours} hours. 
              Completing too quickly may trigger fraud review.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleComplete}
            disabled={!agreed || loading || (isAdmin && !rating)}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              agreed && (!isAdmin || rating)
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? 'Processing...' : isAdmin ? 'Approve & Pay' : 'Complete Job'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default JobCompletionModal;